import {
  GristImportDestination,
  GristImportResult,
  GristImportTableMapping,
} from "app/client/lib/imports/grist/GristImporterTypes";
import {
  DocSchema,
  DocSchemaImportWarning,
  ImportSchemaTransformParams,
  transformImportSchema,
  validateImportSchema,
} from "app/common/DocSchemaImport";
import { GristImportAPIImpl } from "app/common/GristImportAPI";
import { gristImportSchemaFromSourceSchema } from "app/common/imports/grist/GristSchemaImporter";
import { UserAPI } from "app/common/UserAPI";

/**
 * Runs an import from another Grist document.
 *
 * Unlike Airtable imports, most of the import code lives and runs on the server
 * (see app/server/lib/ImportFromGrist.ts). This function only creates the destination
 * document, if needed, and calls the import endpoint.
 */
export async function runGristImport(params: {
  sourceDocId: string,
  userApi: UserAPI,
  tables: GristImportTableMapping[],
  destination: GristImportDestination,
}): Promise<GristImportResult> {
  const { sourceDocId, userApi, tables, destination } = params;

  const docId = destination.type === "existing-doc" ? destination.docId : await userApi.newDoc(
    { name: destination.name }, destination.workspaceId,
  );

  const docApi = userApi.getDocAPI(docId);
  const importApi = new GristImportAPIImpl(docApi.getBaseUrl(), docApi.options);
  const result = await importApi.importFromGrist({
    sourceDocId,
    tables,
    replaceExistingTables: destination.type === "new-doc",
  });

  // TODO: Show the warnings to the user after the import.
  if (result.warnings.length > 0) {
    console.warn({
      message: `Warnings were emitted while importing from Grist doc ${sourceDocId} to ${docId}`,
      docId,
      sourceDocId,
      warnings: result.warnings,
    });
  }

  return { docId, warnings: result.warnings };
}

/**
 * Validates an import with the given transformations, returning any warnings.
 */
export function validateGristSchemaImport(
  sourceSchema: DocSchema,
  transformations?: ImportSchemaTransformParams,
  destDocSchema?: DocSchema,
): DocSchemaImportWarning[] {
  const warnings: DocSchemaImportWarning[] = [];

  const { schema: importSchema, warnings: sourceWarnings } = gristImportSchemaFromSourceSchema(sourceSchema);
  warnings.push(...sourceWarnings);

  const transformedSchema = transformImportSchema(importSchema, transformations ?? {}, destDocSchema);
  warnings.push(
    ...transformedSchema.warnings,
    ...validateImportSchema(transformedSchema.schema, destDocSchema));

  return warnings;
}
